import { ApiError, type ApiRequestOptions } from "./api.ts";

/** One pending invitation as `GET /v1/orgs/{orgId}/members/invitations` returns it. */
export interface PendingInvitation {
  id: string;
  email: string;
  role: "admin" | "member";
  created_at: string;
  expires_at: string;
  inviter?: { user_id: string; display_name: string };
}

/** The Team page's view of the pending invitation list. */
export type InvitationInventory =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | {
      readonly status: "ready";
      readonly invitations: readonly PendingInvitation[];
    };

type Api = <T>(path: string, input?: ApiRequestOptions) => Promise<T>;

export function invitationsPath(organizationId: string): string {
  return `/v1/orgs/${encodeURIComponent(organizationId)}/members/invitations`;
}

export function invitationPath(
  organizationId: string,
  invitationId: string,
): string {
  return `${invitationsPath(organizationId)}/${encodeURIComponent(invitationId)}`;
}

/** Read the organization's pending invitations, newest first. */
export async function readPendingInvitations(
  api: Api,
  organizationId: string,
  signal?: AbortSignal,
): Promise<readonly PendingInvitation[]> {
  const result = await api<{ invitations: PendingInvitation[] }>(
    invitationsPath(organizationId),
    signal === undefined ? {} : { signal },
  );
  return result.invitations;
}

/** The pending invitation already sent to this email, if any. */
export function pendingInvitationFor(
  invitations: readonly PendingInvitation[],
  email: string,
): PendingInvitation | undefined {
  const wanted = email.trim().toLowerCase();
  return invitations.find(
    (invitation) => invitation.email.toLowerCase() === wanted,
  );
}

/** What the invite form says when the server refuses an invitation. */
export type InviteRefusal =
  | { readonly kind: "already_pending" }
  | { readonly kind: "already_member" }
  | { readonly kind: "other"; readonly message: string };

export function inviteRefusal(cause: unknown): InviteRefusal {
  if (cause instanceof ApiError && cause.code === "invitation_pending") {
    return { kind: "already_pending" };
  }
  if (cause instanceof ApiError && cause.code === "already_member") {
    return { kind: "already_member" };
  }
  return {
    kind: "other",
    message:
      cause instanceof Error
        ? cause.message
        : "The request could not be completed.",
  };
}

/**
 * The list after an invitation was just sent: the fresh read wins, and the
 * sent invitation stays visible even if the provider's list has not caught up.
 */
export function withSentInvitation(
  invitations: readonly PendingInvitation[],
  sent: PendingInvitation | undefined,
): readonly PendingInvitation[] {
  if (sent === undefined) return invitations;
  return invitations.some((invitation) => invitation.id === sent.id)
    ? invitations
    : [sent, ...invitations];
}
